import { useMemo } from 'react';

import type { OrderDetailsOrderLine } from '@/types/orderDetailsOrder';

import type { OrderAppStorefrontWithdrawalEligibilityQuery } from '@/graphql/orderApp/api';
import { shouldShowWithdrawalCta } from '@/lib/withdrawal/eligibility';
import type {
  WithdrawalCode,
  WithdrawalEligibility,
} from '@/lib/withdrawal/types';
import { normalizeWithdrawalCode } from '@/lib/withdrawal/withdrawalCode';
import type { OrderDetailsGuestSession } from '@/views/OrderDetailsView/helpers';

const WITHDRAWAL_UI_DISABLED = {
  guestWaitingForEligibilityLookup: false,
  isGuestEligibilityRecaptchaVisible: false,
  isWithdrawalEligibilityLoading: false,
  showGlobalOrderNotEligibleMessage: false,
  showGuestReturnButton: false,
  showWithdrawalCta: false,
  withdrawalBlockedOnlyByOutOfEuScope: false,
  withdrawalCode: null,
  withdrawalEligibility: null,
  withdrawalEligibilityResolvedForUi: true,
} as const;

export type UseOrderWithdrawalUiStateParams = {
  guestEligibilityFetchFailed: boolean;
  guestEligibilityLocal: WithdrawalEligibility | null;
  guestHasCheckedEligibility: boolean;
  guestSession: OrderDetailsGuestSession | undefined;
  hasFulfillment: boolean;
  isOrderInWithdrawalScope?: boolean;
  orderLines?: ReadonlyArray<OrderDetailsOrderLine>;
  type: string | undefined;
  withdrawalEligibilityData:
    | OrderAppStorefrontWithdrawalEligibilityQuery
    | undefined;
  withdrawalEligibilityQueryLoading: boolean;
};

export function useOrderWithdrawalUiState(
  params: UseOrderWithdrawalUiStateParams,
) {
  const {
    guestEligibilityFetchFailed,
    guestEligibilityLocal,
    guestHasCheckedEligibility,
    guestSession,
    hasFulfillment,
    isOrderInWithdrawalScope = true,
    orderLines = [],
    type,
    withdrawalEligibilityData,
    withdrawalEligibilityQueryLoading,
  } = params;

  const orderLinesById = useMemo(
    () => new Map(orderLines.map((line) => [line.id, line])),
    [orderLines],
  );

  return useMemo(() => {
    if (!isOrderInWithdrawalScope) {
      return { ...WITHDRAWAL_UI_DISABLED };
    }

    const resolvedGuestEligibility: WithdrawalEligibility | null = guestSession
      ? (guestSession.eligibility ?? guestEligibilityLocal)
      : null;

    const withdrawalEligibility: WithdrawalEligibility | null = guestSession
      ? resolvedGuestEligibility
      : (() => {
          const withdrawal =
            withdrawalEligibilityData?.withdrawalEligibility?.withdrawal;
          if (!withdrawal) return null;

          return {
            code:
              normalizeWithdrawalCode(withdrawal.code) ?? 'VALIDATION_ERROR',
            lines: withdrawal.lines.map((line) => ({
              orderLineId: line.orderLineId,
              quantity: line.quantity,
              eligibleQuantity: line.eligibleQuantity,
              status: line.status,
              disabledReasonCode: line.disabledReasonCode ?? null,
              windowEndAt: line.windowEndAt ?? null,
            })),
          };
        })();

    const isWithdrawalEligibilityLoading =
      type === 'ORDERHISTORY' &&
      !hasFulfillment &&
      (guestSession
        ? Boolean(guestSession.guestLookup) &&
          guestSession.eligibility == null &&
          !guestHasCheckedEligibility
        : withdrawalEligibilityQueryLoading);

    const showWithdrawalCta =
      type === 'ORDERHISTORY' &&
      !hasFulfillment &&
      shouldShowWithdrawalCta(
        withdrawalEligibility,
        orderLinesById.size > 0 ? orderLinesById : undefined,
      );

    const withdrawalEligibilityResolvedForUi =
      !guestSession ||
      guestSession.eligibility != null ||
      guestHasCheckedEligibility;

    const withdrawalCode: WithdrawalCode | null =
      withdrawalEligibility?.code ??
      normalizeWithdrawalCode(
        withdrawalEligibilityData?.withdrawalEligibility?.errors?.[0]?.code,
      );

    const withdrawalBlockedOnlyByOutOfEuScope =
      type === 'ORDERHISTORY' &&
      !hasFulfillment &&
      Boolean(withdrawalEligibility?.lines?.length) &&
      Boolean(
        withdrawalEligibility?.lines.every(
          (l) => l.disabledReasonCode === 'OUT_OF_EU_SCOPE',
        ),
      );

    const showGlobalOrderNotEligibleMessage =
      type === 'ORDERHISTORY' &&
      !hasFulfillment &&
      !guestEligibilityFetchFailed &&
      !isWithdrawalEligibilityLoading &&
      !showWithdrawalCta &&
      withdrawalEligibilityResolvedForUi &&
      (withdrawalCode == null ||
        withdrawalCode === 'LOGIN_REQUIRED' ||
        withdrawalCode === 'NO_MATCH' ||
        withdrawalCode === 'ACCESS_DENIED' ||
        withdrawalCode === 'VALIDATION_ERROR');

    const isGuestEligibilityRecaptchaVisible =
      Boolean(guestSession) &&
      type === 'ORDERHISTORY' &&
      !hasFulfillment &&
      guestSession?.eligibility == null &&
      Boolean(guestSession?.guestLookup) &&
      (!guestHasCheckedEligibility || guestEligibilityFetchFailed);

    const guestWaitingForEligibilityLookup =
      Boolean(guestSession?.guestLookup) &&
      guestSession?.eligibility == null &&
      !guestHasCheckedEligibility;

    const showGuestReturnButton =
      type === 'ORDERHISTORY' &&
      !hasFulfillment &&
      (showWithdrawalCta ||
        guestWaitingForEligibilityLookup ||
        guestEligibilityFetchFailed);

    return {
      guestWaitingForEligibilityLookup,
      isGuestEligibilityRecaptchaVisible,
      isWithdrawalEligibilityLoading,
      showGlobalOrderNotEligibleMessage,
      showGuestReturnButton,
      showWithdrawalCta,
      withdrawalBlockedOnlyByOutOfEuScope,
      withdrawalCode,
      withdrawalEligibility,
      withdrawalEligibilityResolvedForUi,
    };
  }, [
    guestEligibilityFetchFailed,
    guestEligibilityLocal,
    guestHasCheckedEligibility,
    guestSession,
    hasFulfillment,
    isOrderInWithdrawalScope,
    type,
    orderLinesById,
    withdrawalEligibilityData,
    withdrawalEligibilityQueryLoading,
  ]);
}
